// ── Generic 3D playing card: six faces, real stock thickness, pointer tilt,
//    specular sweep, lift-off shadow and a slam-down reveal.
//    Each game supplies its own back palette and its own front face. ──

import { useEffect, useState } from 'react';
import { motion, useTransform, useAnimationControls, AnimatePresence } from 'framer-motion';
import { useTilt } from '../../lib/battle3d';

const face = (w, h) => ({
  position: 'absolute', left: '50%', top: '50%',
  width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2,
});

/**
 * back  — { deep, mid, rim, gold, glow, emblem } colours of the hidden face
 * front — React node rendered on the revealed face (fills the card)
 * tone  — accent colour of the revealed face (rim glow, impact ring)
 * edge  — [highlight, mid, shadow] colours of the card's cut edges
 */
const Card3D = ({
  revealed, index, onPick, canPick,
  back, front, tone = '#ffd700',
  frontBase = '#0a0600',
  edge = ['#f6e6b4', '#b98f2e', '#6b4d10'],
  isRevealing = false,
  mobile = false,
  width, ratio = 1.5,
  prompt = 'SCEGLI',
  testId,
}) => {
  const W = width ?? (mobile ? 70 : 132);
  const H = Math.round(W * ratio);
  const D = mobile ? 6 : 10;
  const R = mobile ? 6 : 11;

  const interactive = canPick && !revealed;
  const [hover, setHover] = useState(false);
  const [impact, setImpact] = useState(0);
  const { rx, ry, glare, handlers } = useTilt({ max: mobile ? 8 : 14, disabled: !interactive });
  const liftControls = useAnimationControls();

  const sheenPos = useTransform(glare, (g) => `${g * 160 - 30}%`);

  // Lift-off → flip → slam back onto the table
  useEffect(() => {
    if (!isRevealing) return undefined;
    let alive = true;
    liftControls.start({
      z: [0, mobile ? 70 : 130, mobile ? 70 : 130, 0],
      rotateX: [0, -16, -16, 0],
      transition: { duration: 0.9, times: [0, 0.28, 0.72, 1], ease: [0.4, 0, 0.2, 1] },
    }).then(() => { if (alive) setImpact(Date.now()); });
    return () => { alive = false; };
  }, [isRevealing, liftControls, mobile]);

  const lift = hover && interactive ? (mobile ? 22 : 46) : 0;

  return (
    <div
      data-testid={testId ?? `card-${index}`}
      onClick={() => { if (interactive) onPick?.(index); }}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => { setHover(false); handlers.onPointerLeave(); }}
      onPointerMove={handlers.onPointerMove}
      style={{
        width: W, height: H, position: 'relative',
        perspective: mobile ? 700 : 1100,
        cursor: interactive ? 'pointer' : 'default',
        flexShrink: 0, transformStyle: 'preserve-3d',
      }}
    >
      {/* Contact shadow on the table */}
      <motion.div
        animate={{ scale: lift ? 0.82 : 1, opacity: lift ? 0.34 : 0.6, filter: `blur(${lift ? 14 : 6}px)` }}
        transition={{ type: 'spring', stiffness: 260, damping: 26 }}
        style={{
          position: 'absolute', left: '6%', right: '6%', bottom: mobile ? -6 : -10, height: mobile ? 14 : 22,
          borderRadius: '50%', background: 'rgba(0,0,0,0.75)', zIndex: 0,
        }}
      />

      {/* Halo under a pickable card */}
      {interactive && (
        <motion.div
          animate={{ opacity: [0.25, 0.7, 0.25], scale: [0.94, 1.06, 0.94] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: mobile ? -6 : -12, borderRadius: R + 8,
            border: `1px solid ${back.rim}`, boxShadow: `0 0 18px ${back.glow}, inset 0 0 18px ${back.glow}`,
            pointerEvents: 'none', zIndex: 0,
          }}
        />
      )}

      {/* Lift / slam layer */}
      <motion.div animate={liftControls} style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d', position: 'relative', zIndex: 1 }}>
        {/* Pointer tilt layer */}
        <motion.div style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d', rotateX: rx, rotateY: ry }}>
          {/* Hover lift */}
          <motion.div
            animate={{ z: lift, rotateX: lift ? -6 : 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d' }}
          >
            {/* Flip layer */}
            <motion.div
              animate={{ rotateY: revealed ? 180 : 0 }}
              transition={{ duration: 0.62, ease: [0.45, 0, 0.2, 1] }}
              style={{ width: '100%', height: '100%', transformStyle: 'preserve-3d', position: 'relative' }}
            >
              {/* ══ HIDDEN FACE ══ */}
              <div style={{
                ...face(W, H), transform: `translateZ(${D / 2}px)`,
                borderRadius: R, overflow: 'hidden',
                background: `linear-gradient(155deg, ${back.mid} 0%, ${back.deep} 42%, ${back.deep} 62%, ${back.mid} 100%)`,
                boxShadow: `inset 0 0 34px ${back.shade ?? 'rgba(0,0,0,0.92)'}, inset 0 0 8px ${back.shade ?? 'rgba(0,0,0,0.8)'}, 0 0 0 1.5px ${back.rim}`,
              }}>
                <div style={{
                  position: 'absolute', inset: 0, opacity: 0.15,
                  backgroundImage: `repeating-linear-gradient(45deg, ${back.rim} 0 1px, transparent 1px 9px),
                                    repeating-linear-gradient(-45deg, ${back.rim} 0 1px, transparent 1px 9px)`,
                }} />
                <div style={{ position: 'absolute', inset: mobile ? 4 : 7, borderRadius: R - 3, border: `1px solid ${back.gold}55` }} />
                <div style={{ position: 'absolute', inset: mobile ? 6 : 10, borderRadius: R - 4, border: `1px solid ${back.gold}22` }} />

                {/* embossed medallion */}
                <div style={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  width: mobile ? 30 : 58, height: mobile ? 30 : 58, borderRadius: '50%',
                  background: `radial-gradient(circle at 40% 32%, ${back.gold}33, transparent 68%)`,
                  border: `1px solid ${back.gold}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: `inset 0 2px 6px rgba(0,0,0,0.8), 0 1px 0 ${back.gold}22`,
                }}>
                  <span style={{ fontSize: mobile ? 15 : 27, color: `${back.gold}cc`, textShadow: '0 2px 3px rgba(0,0,0,0.9)' }}>
                    {back.emblem ?? '♛'}
                  </span>
                </div>

                {!mobile && [['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']].map(([v, h], i) => (
                  <div key={i} style={{
                    position: 'absolute', [v]: 6, [h]: 6, width: 7, height: 7, borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 30%, ${back.gold}, ${back.rim} 60%, #000)`,
                    boxShadow: `0 0 5px ${back.rim}`,
                  }} />
                ))}

                <motion.div style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  backgroundImage: `linear-gradient(105deg, transparent 40%, ${back.sheen ?? 'rgba(255,255,255,0.14)'} 50%, transparent 60%)`,
                  backgroundSize: '260% 100%', backgroundPositionX: sheenPos,
                }} />

                {interactive && (
                  <motion.div
                    animate={{ opacity: [0.1, 0.4, 0.1] }}
                    transition={{ duration: 1.9, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, transparent 38%, ${back.rim}33 58%, transparent 88%)` }}
                  />
                )}
              </div>

              {/* ══ REVEALED FACE ══ */}
              <div style={{
                ...face(W, H), transform: `rotateY(180deg) translateZ(${D / 2}px)`,
                borderRadius: R, overflow: 'hidden', background: frontBase,
                boxShadow: `inset 0 0 20px rgba(0,0,0,0.6), 0 0 0 1.5px ${tone}88, 0 0 18px ${tone}44`,
              }}>
                {front}
                <div style={{ position: 'absolute', inset: mobile ? 3 : 5, borderRadius: R - 3, border: `1px solid ${tone}44`, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', inset: 0, borderRadius: R, boxShadow: `inset 0 0 ${mobile ? 14 : 26}px ${tone}55`, pointerEvents: 'none' }} />
              </div>

              {/* ══ CUT EDGES ══ */}
              <div style={{ ...face(D, H), transform: `rotateY(90deg) translateZ(${W / 2}px)`, background: `linear-gradient(to bottom, ${edge[0]}, ${edge[1]} 40%, ${edge[2]})` }} />
              <div style={{ ...face(D, H), transform: `rotateY(-90deg) translateZ(${W / 2}px)`, background: `linear-gradient(to bottom, ${edge[2]}, ${edge[1]} 60%, ${edge[0]})` }} />
              <div style={{ ...face(W, D), transform: `rotateX(90deg) translateZ(${H / 2}px)`, background: `linear-gradient(to right, ${edge[2]}, ${edge[0]} 50%, ${edge[2]})` }} />
              <div style={{ ...face(W, D), transform: `rotateX(-90deg) translateZ(${H / 2}px)`, background: `linear-gradient(to right, ${edge[2]}, ${edge[1]} 50%, ${edge[2]})` }} />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Slam impact ring */}
      <AnimatePresence>
        {impact > 0 && (
          <motion.div
            key={impact}
            initial={{ opacity: 0.85, scale: 0.4 }}
            animate={{ opacity: 0, scale: 2.1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
            onAnimationComplete={() => setImpact(0)}
            style={{
              position: 'absolute', left: '50%', bottom: -6, width: W, height: W * 0.36,
              marginLeft: -W / 2, borderRadius: '50%',
              border: `2px solid ${tone}`, boxShadow: `0 0 24px ${tone}`,
              pointerEvents: 'none', zIndex: 3,
            }}
          />
        )}
      </AnimatePresence>

      {/* Pick prompt */}
      {interactive && !mobile && prompt && (
        <motion.div
          animate={{ y: [0, -4, 0], opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', bottom: -26, left: '50%', transform: 'translateX(-50%)',
            padding: '2px 10px', borderRadius: 6, whiteSpace: 'nowrap',
            background: `${back.rim}22`, border: `1px solid ${back.rim}77`,
            fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', color: back.gold,
            pointerEvents: 'none', zIndex: 3,
          }}
        >
          {prompt}
        </motion.div>
      )}
    </div>
  );
};

export default Card3D;
